import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  CAPTURE_DESTINATIONS,
  type CaptureDestination,
  type CaptureItem,
  type CaptureToken,
} from '@almanac/contracts';
import { SHORTCUTS, ShortcutInstaller } from '../components/ShortcutInstaller';
import { CaptureContent, createCapturePreviewLoader } from './CaptureContent';
import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthContext';
import { webConfig } from '../config';
import { useCopyToClipboard } from '../lib/copy';

const DESCRIPTIONS = {
  inbox: 'Keep it here to decide later.',
  research: 'Queue private research on this link or question.',
  todo: 'Create a to-do you can track in Inputs.',
  guidance: 'Save standing instructions for your assistant.',
};
const LABELS = { inbox: 'Inbox', todo: 'To-dos', research: 'Research queue', guidance: 'Guidance' };
const FIELD =
  'w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring sm:py-2 sm:text-sm';
const BUTTON =
  'rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50';
const SECONDARY =
  'rounded-lg bg-muted px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/70 focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50';

/** Session changes discard token plaintext and any outstanding capture responses. */
export function CapturePage() {
  const { sessionVersion } = useAuth();
  return <CaptureSession key={sessionVersion} />;
}

/** Private inbox and personal Shortcut setup share the same durable capture pipeline. */
function CaptureSession() {
  const api = useApi();
  const loadPreview = useMemo(() => createCapturePreviewLoader(api.loadCapturePreview), [api]);
  const { copy, copiedKey, copyError } = useCopyToClipboard();
  const [install, setInstall] = useState<keyof typeof SHORTCUTS | null>(null);
  const [items, setItems] = useState<CaptureItem[]>([]);
  const [tokens, setTokens] = useState<CaptureToken[]>([]);
  const [secret, setSecret] = useState<{ token: string; id: string } | null>(null);
  const [text, setText] = useState('');
  const [destination, setDestination] = useState<CaptureDestination>('research');
  const [view, setView] = useState<'inbox' | 'filed' | 'all' | 'archived'>('inbox');
  const [setupOverride, setSetupOverride] = useState<boolean | null>(null);
  const [label, setLabel] = useState('iPhone share sheet');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const alive = useRef(true);
  const retry = useRef<{ text: string; destination: CaptureDestination; key: string } | null>(null);
  const setupOpen = setupOverride ?? (!loading && items.length === 0 && tokens.length === 0);
  const liveItems = items.filter((item) => !item.archived_at);
  const inboxCount = liveItems.filter((item) => item.destination === 'inbox').length;
  const visibleItems = items.filter((item) => {
    if (view === 'archived') return Boolean(item.archived_at);
    if (item.archived_at) return false;
    if (view === 'inbox') return item.destination === 'inbox';
    if (view === 'filed') return item.destination !== 'inbox';
    return true;
  });
  let emptyMessage = 'No captures in this view yet.';
  if (view === 'archived') emptyMessage = 'Nothing archived.';
  else if (items.length === 0)
    emptyMessage = 'Your Inbox is empty. Share something to get started.';
  else if (view === 'inbox') {
    if (more) emptyMessage = 'No inbox items shown. Load older captures to see earlier shares.';
    else emptyMessage = 'All caught up. Your filed captures are still available in Filed.';
  }
  const endpoint = new URL('/api/capture', webConfig.apiBaseUrl || window.location.origin).href;

  useEffect(() => {
    alive.current = true;
    void Promise.all([api.listCaptures(), api.listCaptureTokens()])
      .then(([captures, records]) => {
        if (!alive.current) return;
        setItems(captures.captures);
        setMore(captures.captures.length === 100);
        setTokens(records.tokens);
        return undefined;
      })
      .catch((caught: unknown) => {
        if (alive.current)
          setError(caught instanceof Error ? caught.message : 'Could not load captures.');
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
    return () => {
      alive.current = false;
    };
  }, [api]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await action();
    } catch (caught) {
      if (alive.current) setError(caught instanceof Error ? caught.message : 'Action failed.');
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return;
    await run(async () => {
      if (retry.current?.text !== text || retry.current.destination !== destination)
        retry.current = { text, destination, key: crypto.randomUUID() };
      const { capture } = await api.createCapture({
        text,
        destination,
        idempotency_key: retry.current.key,
      });
      if (!alive.current) return;
      setItems((current) => [
        capture,
        ...current.filter((item) => item.capture_id !== capture.capture_id),
      ]);
      setView(capture.destination === 'inbox' ? 'inbox' : 'filed');
      setText('');
      retry.current = null;
      setMessage(`Saved privately to ${LABELS[capture.destination]}.`);
      window.setTimeout(() => document.getElementById(capture.capture_id)?.focus(), 0);
    });
  }
  async function move(item: CaptureItem, nextDestination: CaptureDestination) {
    await run(async () => {
      await api.fileCapture(item.capture_id, nextDestination);
      const { captures } = await api.listCaptures();
      if (!alive.current) return;
      setItems(captures);
      setMore(captures.length === 100);
      setMessage(`Moved to ${LABELS[nextDestination]}. Original input is preserved.`);
    });
  }
  async function setArchived(item: CaptureItem, archived: boolean) {
    await run(async () => {
      let response;
      if (archived) response = await api.archiveCapture(item.capture_id);
      else response = await api.restoreCapture(item.capture_id);
      const { capture } = response;
      if (!alive.current) return;
      // The response omits history and cached previews; keep what the card already shows.
      setItems((current) =>
        current.map((entry) => {
          if (entry.capture_id !== capture.capture_id) return entry;
          return { ...capture, corrections: entry.corrections, x_preview: entry.x_preview };
        }),
      );
      if (!archived) setMessage(`Restored to ${LABELS[capture.destination]}.`);
      else if (capture.destination === 'inbox') setMessage('Archived. Restore it from Archived.');
      else
        setMessage(
          `Archived with its ${LABELS[capture.destination]} item. Restore it from Archived.`,
        );
    });
  }
  async function mint(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const result = await api.mintCaptureToken(label);
      if (!alive.current) return;
      setSetupOverride(true);
      setSecret({ token: result.token, id: result.record.token_id });
      setTokens((current) => [result.record, ...current]);
    });
  }
  async function revoke(id: string) {
    await run(async () => {
      await api.revokeCaptureToken(id);
      if (!alive.current) return;
      setTokens((current) =>
        current.map((record) =>
          record.token_id === id ? { ...record, revoked_at: new Date().toISOString() } : record,
        ),
      );
      setSecret((current) => (current?.id === id ? null : current));
      setMessage('Token revoked. Shares using it will now be rejected.');
    });
  }

  return (
    <main className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6">
      <div className="grid grid-cols-[1fr_auto] items-start gap-x-3 gap-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-balance">Inbox</h1>
        <button
          type="button"
          className={SECONDARY}
          aria-expanded={setupOpen}
          aria-controls="sharing-setup"
          onClick={() => {
            if (setupOpen) setSecret(null);
            setSetupOverride(!setupOpen);
            if (!setupOpen)
              window.requestAnimationFrame(() =>
                document.getElementById('sharing-setup')?.scrollIntoView?.({ behavior: 'smooth' }),
              );
          }}
        >
          {setupOpen ? 'Hide sharing setup' : 'Sharing setup'}
        </button>
        <p className="col-span-full max-w-[60ch] text-base text-pretty text-muted-foreground sm:text-sm">
          Your private collection of shared links and notes. Keep something here, or send it to
          research, to-dos, or guidance.
        </p>
      </div>
      {error || copyError ? (
        <p role="alert" className="rounded-lg bg-danger/10 p-3 text-danger">
          {error || copyError}
        </p>
      ) : null}
      {message ? (
        <output className="block rounded-lg bg-success/10 p-3 text-success">{message}</output>
      ) : null}
      <section
        aria-labelledby="capture-heading"
        className="space-y-4 rounded-xl bg-card p-4 ring-1 ring-foreground/10 sm:p-5"
      >
        <h2 id="capture-heading" className="text-lg font-semibold">
          Add a link or note
        </h2>
        <form
          onSubmit={(event) => {
            void submit(event);
          }}
          className="space-y-4"
        >
          <textarea
            name="capture-text"
            aria-label="Link or text to capture"
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={20_000}
            rows={3}
            className={FIELD}
            placeholder="Paste a link, ask a research question, or write a note…"
            disabled={busy}
            required
          />
          <fieldset disabled={busy} className="space-y-2">
            <legend className="mb-2 text-base font-medium sm:text-sm">Destination</legend>
            <div className="flex flex-wrap gap-2">
              {(['research', 'inbox', 'todo', 'guidance'] as const).map((option) => (
                <label key={option} className="cursor-pointer">
                  <input
                    type="radio"
                    name="capture-destination"
                    value={option}
                    checked={destination === option}
                    onChange={() => setDestination(option)}
                    className="peer sr-only"
                  />
                  <span className="inline-flex min-h-10 items-center rounded-lg bg-muted/60 px-3 py-2 ring-1 ring-transparent peer-checked:bg-primary/10 peer-checked:text-foreground peer-checked:ring-primary/50 peer-focus-visible:outline-2 peer-focus-visible:outline-ring peer-disabled:opacity-50">
                    {LABELS[option]}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-base text-muted-foreground sm:text-sm">
              {DESCRIPTIONS[destination]}
            </p>
          </fieldset>
          <button type="submit" className={BUTTON} disabled={busy || !text.trim()}>
            Save privately
          </button>
        </form>
      </section>
      <section aria-labelledby="inbox-heading" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="inbox-heading" className="text-lg font-semibold">
            Your captures
          </h2>
          <button
            type="button"
            disabled={busy}
            className={SECONDARY}
            onClick={() => {
              void run(async () => {
                const response = await api.listCaptures();
                if (alive.current) {
                  setItems(response.captures);
                  setMore(response.captures.length === 100);
                }
              });
            }}
          >
            Refresh
          </button>
        </div>
        <fieldset className="flex flex-wrap gap-2 text-base sm:text-sm">
          <legend className="sr-only">Capture views</legend>
          {(
            [
              ['inbox', more ? 'Inbox' : `Inbox (${inboxCount})`],
              ['filed', more ? 'Filed' : `Filed (${liveItems.length - inboxCount})`],
              ['all', 'All captures'],
              ['archived', 'Archived'],
            ] as const
          ).map(([option, viewLabel]) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => setView(option)}
              className="rounded-lg px-3 py-2 text-muted-foreground hover:bg-muted/60 aria-pressed:bg-muted aria-pressed:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
            >
              {viewLabel}
            </button>
          ))}
        </fieldset>
        <p className="text-base text-muted-foreground sm:text-sm">
          {view === 'inbox' ? 'Waiting for you to choose a destination.' : null}
          {view === 'archived'
            ? 'Hidden from your Inbox, with any filed to-do or research paused. Sharing the same thing again restores it.'
            : null}
          {view === 'filed' || view === 'all'
            ? 'Every capture keeps its original share. Filed items link to their destination.'
            : null}
        </p>
        {loading ? (
          <p>Loading…</p>
        ) : visibleItems.length === 0 ? (
          <div className="rounded-xl bg-card p-6 text-base text-muted-foreground ring-1 ring-foreground/10 sm:text-sm">
            {emptyMessage}
          </div>
        ) : null}
        {visibleItems.map((item) => {
          return (
            <article
              key={item.capture_id}
              id={item.capture_id}
              tabIndex={-1}
              className="space-y-3 rounded-xl bg-card p-4 ring-1 ring-foreground/10 focus:outline-2 focus:outline-ring sm:p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 text-base sm:text-sm">
                <p className="rounded-md bg-muted px-2 py-1 font-medium">
                  {LABELS[item.destination]}
                </p>
                <p className="text-muted-foreground">
                  <time dateTime={item.created_at}>
                    {new Date(item.created_at).toLocaleString()}
                  </time>
                </p>
              </div>
              <CaptureContent item={item} loadPreview={loadPreview} />
              {item.archived_at ? (
                <div className="flex flex-wrap items-center gap-3 border-t border-foreground/10 pt-3 text-base sm:text-sm">
                  <p className="text-muted-foreground">
                    Archived{' '}
                    <time dateTime={item.archived_at}>
                      {new Date(item.archived_at).toLocaleString()}
                    </time>
                  </p>
                  <button
                    type="button"
                    className={SECONDARY}
                    disabled={busy}
                    aria-label={`Restore ${item.title}`}
                    onClick={() => {
                      void setArchived(item, false);
                    }}
                  >
                    Restore
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3 border-t border-foreground/10 pt-3 text-base sm:text-sm">
                  <label className="flex flex-wrap items-center gap-2">
                    Destination
                    <select
                      name={`destination-${item.capture_id}`}
                      aria-label={`Destination for ${item.title}`}
                      value={item.destination}
                      onChange={(event) => {
                        void move(item, event.target.value as CaptureDestination);
                      }}
                      disabled={busy}
                      className="rounded-lg bg-muted px-2 py-2 text-base ring-1 ring-foreground/10 focus-visible:outline-2 focus-visible:outline-ring sm:text-sm"
                    >
                      {CAPTURE_DESTINATIONS.map((option) => (
                        <option key={option} value={option}>
                          {LABELS[option]}
                        </option>
                      ))}
                    </select>
                  </label>
                  {item.destination !== 'inbox' && item.canonical_ids[item.destination] ? (
                    <Link
                      className="text-primary underline underline-offset-4 hover:text-foreground"
                      to={`/inputs?tab=${item.destination === 'todo' ? 'todos' : item.destination}#${item.destination === 'todo' ? 'todo' : 'input'}-${item.canonical_ids[item.destination]}`}
                    >
                      Open in {LABELS[item.destination]}
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    className="ml-auto text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-50"
                    disabled={busy}
                    aria-label={`Archive ${item.title}`}
                    onClick={() => {
                      void setArchived(item, true);
                    }}
                  >
                    Archive
                  </button>
                </div>
              )}
              {item.routing_failed && !item.archived_at ? (
                <div className="rounded-lg bg-warning/10 p-3 text-base text-warning sm:text-sm">
                  Saved in Inbox; filing failed.{' '}
                  <button
                    type="button"
                    className="underline"
                    disabled={busy}
                    onClick={() => {
                      void move(item, item.requested_destination);
                    }}
                  >
                    Retry {LABELS[item.requested_destination]}
                  </button>
                </div>
              ) : null}
              <details className="text-base text-muted-foreground sm:text-sm">
                <summary className="cursor-pointer">
                  Original share
                  {item.corrections.length
                    ? ` · ${item.corrections.length} destination changes`
                    : ''}
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-all rounded-lg bg-muted/50 p-3">
                  {[item.raw_input.title, item.raw_input.text, item.raw_input.url]
                    .filter(Boolean)
                    .join('\n')}
                </pre>
                {item.corrections.map((entry, index) => (
                  <p key={`${entry.created_at}:${index}`}>
                    {LABELS[entry.previous_destination]} → {LABELS[entry.destination]} ·{' '}
                    {new Date(entry.created_at).toLocaleString()}
                  </p>
                ))}
              </details>
            </article>
          );
        })}
        {more && items.at(-1) ? (
          <button
            type="button"
            disabled={busy}
            className={SECONDARY}
            onClick={() => {
              void run(async () => {
                const response = await api.listCaptures(items.at(-1)!);
                if (alive.current) {
                  setItems((current) => [...current, ...response.captures]);
                  setMore(response.captures.length === 100);
                }
              });
            }}
          >
            Load older captures
          </button>
        ) : null}
      </section>
      {setupOpen ? (
        <section
          id="sharing-setup"
          aria-labelledby="shortcut-heading"
          className="space-y-4 rounded-xl bg-card p-4 ring-1 ring-foreground/10 sm:p-5"
        >
          <h2 id="shortcut-heading" className="text-lg font-semibold">
            Set up iPhone sharing
          </h2>
          <p className="text-base text-muted-foreground sm:text-sm">
            Create one personal token and install either or both shortcuts. It can only add captures
            to your account. It cannot read your content or publish anything. Keep it private;
            revoke it here whenever needed.
          </p>
          <form
            onSubmit={(event) => {
              void mint(event);
            }}
            className="flex flex-wrap items-end gap-3"
          >
            <label className="min-w-0 flex-1 text-base sm:text-sm">
              Token label
              <input
                name="token-label"
                className={`${FIELD} mt-1`}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                maxLength={80}
                required
              />
            </label>
            <button
              type="submit"
              disabled={busy || Boolean(secret) || !label.trim()}
              className={SECONDARY}
            >
              Create token
            </button>
          </form>
          {secret ? (
            <div className="space-y-2 rounded-lg bg-muted p-3">
              <p className="text-sm font-medium">
                Shown once. Copy it into your personal Shortcut.
              </p>
              <input
                name="capture-token"
                aria-label="New capture token"
                readOnly
                value={secret.token}
                onFocus={(event) => event.target.select()}
                className={`${FIELD} font-mono text-sm`}
                autoComplete="off"
              />
              <div className="flex gap-4">
                <button
                  type="button"
                  className="text-sm text-primary"
                  onClick={() => copy('token', secret.token)}
                >
                  {copiedKey === 'token' ? 'Copied' : 'Copy token'}
                </button>
                <button
                  type="button"
                  className="text-sm text-primary"
                  onClick={() => setSecret(null)}
                >
                  I saved it — hide token
                </button>
              </div>
            </div>
          ) : null}
          <label className="block text-base sm:text-sm">
            Your endpoint
            <input
              name="capture-endpoint"
              aria-label="Capture endpoint"
              readOnly
              value={endpoint}
              onFocus={(event) => event.target.select()}
              className={`${FIELD} mt-1 font-mono text-sm`}
            />
          </label>
          <button
            type="button"
            className="text-sm text-primary"
            onClick={() => copy('endpoint', endpoint)}
          >
            {copiedKey === 'endpoint' ? 'Copied' : 'Copy endpoint'}
          </button>
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={(event) => {
                event.currentTarget.focus();
                setInstall('research');
              }}
              className="space-y-2 rounded-lg text-left bg-primary/10 p-4 ring-1 ring-primary/20 hover:bg-primary/15"
            >
              <span className="block font-semibold">Research in Almanac</span>
              <span className="block text-base text-muted-foreground sm:text-sm">
                Add an optional question, then send the share straight to your private research
                queue.
              </span>
              <span className="block text-base font-medium text-primary sm:text-sm">
                Download research shortcut
              </span>
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.currentTarget.focus();
                setInstall('inbox');
              }}
              className="space-y-2 rounded-lg text-left bg-muted/50 p-4 ring-1 ring-foreground/10 hover:bg-muted"
            >
              <span className="block font-semibold">Save to Almanac Inbox</span>
              <span className="block text-base text-muted-foreground sm:text-sm">
                Add an optional note and save the share here to decide later.
              </span>
              <span className="block text-base font-medium text-primary sm:text-sm">
                Download inbox shortcut
              </span>
            </button>
          </div>
          <div className="space-y-2 text-base text-muted-foreground sm:text-sm">
            <p>
              Choose a shortcut to open its install sheet. Almanac stays here, and you can cancel or
              go back at any time. You can also copy a download link and paste it into Safari.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              <button
                type="button"
                className="min-h-10 text-primary underline underline-offset-4"
                onClick={() =>
                  copy(
                    'research-shortcut',
                    new URL('/Research-in-Almanac.shortcut', window.location.origin).href,
                  )
                }
              >
                {copiedKey === 'research-shortcut'
                  ? 'Research link copied'
                  : 'Copy research download link'}
              </button>
              <button
                type="button"
                className="min-h-10 text-primary underline underline-offset-4"
                onClick={() =>
                  copy(
                    'inbox-shortcut',
                    new URL('/Save-to-Almanac-Inbox.shortcut', window.location.origin).href,
                  )
                }
              >
                {copiedKey === 'inbox-shortcut' ? 'Inbox link copied' : 'Copy inbox download link'}
              </button>
            </div>
          </div>
          <p className="text-base text-muted-foreground sm:text-sm">
            On import, paste your token after “Bearer” in the Authorization header when prompted.
            Both shortcuts use the production Almanac endpoint and ask for an optional note after
            you share. For a different environment, use your endpoint above in the manual recipe.
          </p>
          <details>
            <summary className="cursor-pointer font-medium">Build the Shortcut by hand</summary>
            <ol className="mt-3 list-decimal space-y-3 pl-5 text-base sm:text-sm">
              <li>
                Open Shortcuts, create “Share to Almanac”, and enable{' '}
                <strong>Show in Share Sheet</strong>. Accept URLs, Text, and Safari Web Pages.
              </li>
              <li>
                Add <strong>Get Text from Input</strong> using <strong>Shortcut Input</strong>, then
                <strong> Ask for Input</strong> for an optional note. Combine the note and shared
                text in a <strong>Text</strong> action, starting with <code>research:</code> or{' '}
                <code>inbox:</code>.
              </li>
              <li>
                Add <strong>Get Contents of URL</strong> using the endpoint above. Set method to{' '}
                <strong>POST</strong>, header <code>Authorization</code> to{' '}
                <code>Bearer YOUR_TOKEN</code>, and request body to <strong>JSON</strong>. Set the
                JSON field <code>text</code> to the combined text from the preceding action.
              </li>
              <li>
                Add <strong>Show Notification</strong> with <strong>Contents of URL</strong> as its
                body. Share a link or text from another app and check that it appears in Your
                captures above.
              </li>
            </ol>
            <p className="mt-3 text-base text-muted-foreground sm:text-sm">
              Maximum 32 KiB per request and 60 new captures per hour. Identical shares and retries
              return the original save. X post links are enriched with a private preview when
              available. Other links are saved as shared, and ambiguous shares stay in Inbox.
            </p>
          </details>
          <div className="space-y-3">
            <h3 className="font-medium">Your tokens</h3>
            {tokens.filter((token) => !token.revoked_at).length === 0 ? (
              <p className="text-base text-muted-foreground sm:text-sm">No active tokens.</p>
            ) : null}
            {tokens.map((token) => (
              <div
                key={token.token_id}
                className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3"
              >
                <div>
                  <p className="text-sm">
                    {token.label}
                    {token.revoked_at ? ' · Revoked' : ''}
                  </p>
                  <p className="text-base text-muted-foreground sm:text-sm">
                    Last used:{' '}
                    {token.last_used_at ? new Date(token.last_used_at).toLocaleString() : 'Never'}
                  </p>
                </div>
                {!token.revoked_at ? (
                  <button
                    type="button"
                    disabled={busy}
                    className="text-sm text-danger"
                    onClick={() => {
                      void revoke(token.token_id);
                    }}
                  >
                    Revoke
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {install ? (
        <ShortcutInstaller
          key={install}
          shortcut={SHORTCUTS[install]}
          onClose={() => setInstall(null)}
        />
      ) : null}
    </main>
  );
}
